import React, { useMemo } from 'react';
import '../styles/CompMapBubble.css';
import { StatusDot } from '../assets/icons';
import { colour, statusDotColours, formatterCompact } from '../styles/GlobalStyles';

function CompMapBubble(props) {
    const price = props.price;
    const listingStatus = props.listingStatus;
    const isSelected = props.isSelected;
    const isClicked = props.isClicked;
    const isHovered = props.isHovered || false;
    const isCeiling = props.isCeiling || false;
    const isFloor = props.isFloor || false;
    const isStaleActive = props.isStaleActive || false;
    const className = props.className || '';

    // Determine status dot color and stroke color based on listingStatus
    const statusDotStyles = useMemo(() => {
        if (listingStatus === "for_sale") {
            return {
                fill: statusDotColours.forSale,
                stroke: colour.pink01
            };
        } else if (listingStatus === "pending") {
            return {
                fill: statusDotColours.pending,
                stroke: colour.aqua01
            };
        } else {
            // Default to sold
            return {
                fill: statusDotColours.sold,
                stroke: colour.yellow01
            };
        }
    }, [listingStatus]);

    // Determine bubble backgroundColor, borderColor, borderWidth, and text color class based on state
    const bubbleStyles = useMemo(() => {
        // Role variants take priority. Ceiling = red (caps from above);
        // stale active = orange (softer downward signal — market won't pay
        // this asking price); floor = jade (supports from below). All
        // override the selected/clicked/hover states so role stays visible
        // at a glance.
        if (isCeiling && !isClicked && !isHovered) {
            // Price ceiling, Not clicked, Not hovered
            return {
                backgroundColor: colour.red01,
                borderColor: colour.red02,
                borderWidth: "0.5px",
                textColorClass: "colour-red-03"
            };
        } else if (isCeiling && (isClicked || isHovered)) {
            // Price ceiling, Clicked or Hovered
            return {
                backgroundColor: colour.red02,
                borderColor: colour.red03,
                borderWidth: isClicked ? "2px" : "1px",
                textColorClass: "colour-primary"
            };
        } else if (isStaleActive && !isClicked && !isHovered) {
            // Stale active — Phase 4 doubt-pool listing pulling ARV down.
            return {
                backgroundColor: colour.orange01,
                borderColor: colour.orange02,
                borderWidth: "0.5px",
                textColorClass: "colour-orange"
            };
        } else if (isStaleActive && (isClicked || isHovered)) {
            return {
                backgroundColor: colour.orange02,
                borderColor: colour.orange03,
                borderWidth: isClicked ? "2px" : "1px",
                textColorClass: "colour-primary"
            };
        } else if (isFloor && !isClicked && !isHovered) {
            // Price floor — jade counterpart to red ceiling.
            return {
                backgroundColor: colour.jade01,
                borderColor: colour.jade02,
                borderWidth: "0.5px",
                textColorClass: "colour-jade-03"
            };
        } else if (isFloor && (isClicked || isHovered)) {
            return {
                backgroundColor: colour.jade02,
                borderColor: colour.jade03,
                borderWidth: isClicked ? "2px" : "1px",
                textColorClass: "colour-primary"
            };
        } else if (!isSelected && !isClicked && !isHovered) {
            // Not selected, Not clicked, Not hovered
            return {
                backgroundColor: colour.gray02,
                borderColor: colour.gray03,
                borderWidth: "0.5px",
                textColorClass: "colour-secondary"
            };
        } else if (!isSelected && (isClicked || isHovered)) {
            // Not selected, Clicked or Hovered
            return {
                backgroundColor: colour.gray04,
                borderColor: colour.gray05,
                borderWidth: isClicked ? "2px" : "1px",
                textColorClass: "colour-primary"
            };
        } else if (isSelected && !isClicked && !isHovered) {
            // Selected, Not clicked, Not hovered
            return {
                backgroundColor: colour.jade01,
                borderColor: colour.jade02,
                borderWidth: "0.5px",
                textColorClass: "colour-jade-03"
            };
        } else {
            // Selected, Clicked or Hovered
            return {
                backgroundColor: colour.jade02,
                borderColor: colour.jade03,
                borderWidth: isClicked ? "2px" : "1px",
                textColorClass: "colour-primary"
            };
        }
    }, [isSelected, isClicked, isCeiling, isFloor, isStaleActive, isHovered]);

    // Format price if it's a number
    const formattedPrice = useMemo(() => {
        if (typeof price === 'number') {
            return formatterCompact.format(price);
        }
        return price;
    }, [price]);

    return (
        <div 
            className={`comp-map-bubble-container ${className}`}
            style={{
                backgroundColor: bubbleStyles.backgroundColor,
                border: `${bubbleStyles.borderWidth} solid ${bubbleStyles.borderColor}`,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                padding: '2px 4px'
            }}
        >
            <StatusDot
                className="comp-map-bubble-status-dot"
                fill={statusDotStyles.fill}
                stroke={statusDotStyles.stroke}
            />
            <span 
                className={`label-bold comp-map-bubble-price ${bubbleStyles.textColorClass}`}
            >
                {formattedPrice}
            </span>
        </div>
    );
}

export default React.memo(CompMapBubble);

